import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    AlignCenter, AlignLeft, ArrowDown, ArrowUp, Bold, Copy, Heading2, Highlighter, Image as ImageIcon,
    Italic, Link2, List, ListOrdered, Minus, Package, Quote, Stethoscope, Table as TableIcon, Trash2,
    Type, Underline, Video, AlertTriangle, Info, Lightbulb, HelpCircle, Images, MousePointerClick,
} from "lucide-react";
import { rhm } from "../api";
import { Btn, Field, Input, Modal, Select, Textarea } from "../ui";
import { MediaLibrary } from "./MediaLibrary";
import { sanitizeHtml } from "../../lib/sanitize";

export const BLOCK_TYPES = [
    { type: "heading", label: "Heading", icon: Heading2, group: "Text" },
    { type: "text", label: "Text", icon: Type, group: "Text" },
    { type: "quote", label: "Quote", icon: Quote, group: "Text" },
    { type: "tip", label: "Tip box", icon: Lightbulb, group: "Callouts" },
    { type: "info", label: "Important information", icon: Info, group: "Callouts" },
    { type: "warning", label: "Warning box", icon: AlertTriangle, group: "Callouts" },
    { type: "image", label: "Image", icon: ImageIcon, group: "Media" },
    { type: "gallery", label: "Image gallery", icon: Images, group: "Media" },
    { type: "video", label: "Video / embed", icon: Video, group: "Media" },
    { type: "table", label: "Table", icon: TableIcon, group: "Structured" },
    { type: "faq", label: "FAQ", icon: HelpCircle, group: "Structured" },
    { type: "divider", label: "Divider", icon: Minus, group: "Structured" },
    { type: "product", label: "Product card", icon: Package, group: "ReHealthMe" },
    { type: "consultation", label: "Consultation CTA", icon: Stethoscope, group: "ReHealthMe" },
    { type: "cta", label: "Call to action", icon: MousePointerClick, group: "ReHealthMe" },
];

const rid = () => `blk_${Math.random().toString(36).slice(2, 10)}`;

export const newBlock = (type) => {
    const base = { id: rid(), type };
    switch (type) {
        case "heading": return { ...base, level: 2, text: "New section heading" };
        case "text": return { ...base, html: "" };
        case "quote": return { ...base, html: "", cite: "" };
        case "tip": return { ...base, title: "Tip", html: "" };
        case "info": return { ...base, title: "Important information", html: "" };
        case "warning": return { ...base, title: "Please note", html: "" };
        case "image": return { ...base, url: "", alt: "", caption: "", align: "center", width: "full", link: "", new_tab: false };
        case "gallery": return { ...base, images: [] };
        case "video": return { ...base, url: "", caption: "" };
        case "table": return { ...base, columns: ["Column A", "Column B"], rows: [["", ""]] };
        case "faq": return { ...base, title: "Frequently asked questions", items: [{ question: "", answer: "" }] };
        case "product": return { ...base, slug: "redrive-max" };
        case "consultation": return {
            ...base, title: "Need personalised guidance?",
            body: "Get a consultation based on your individual concerns.",
            label: "Choose your consultation", area: "",
        };
        case "cta": return { ...base, title: "Ready to start?", html: "", label: "Shop now", href: "/shop" };
        default: return base;
    }
};

// ---------------------------------------------------------------- rich text
export const RichText = ({ value = "", onChange, testId = "rich-text", minHeight = 120,
    placeholder = "Write here…" }) => {
    const ref = useRef(null);
    const [linkOpen, setLinkOpen] = useState(false);
    const savedRange = useRef(null);

    useEffect(() => {
        if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = sanitizeHtml(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const exec = (cmd, arg) => {
        ref.current?.focus();
        document.execCommand(cmd, false, arg);
        onChange(ref.current.innerHTML);
    };

    const openLink = () => {
        const sel = window.getSelection();
        savedRange.current = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
        setLinkOpen(true);
    };

    const applyLink = (href, label) => {
        ref.current?.focus();
        const sel = window.getSelection();
        if (savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
        if (sel.isCollapsed) document.execCommand("insertHTML", false, `<a href="${href}">${label || href}</a>`);
        else document.execCommand("createLink", false, href);
        onChange(ref.current.innerHTML);
        setLinkOpen(false);
    };

    const TOOLS = [
        { cmd: "bold", icon: Bold, label: "Bold" },
        { cmd: "italic", icon: Italic, label: "Italic" },
        { cmd: "underline", icon: Underline, label: "Underline" },
        { cmd: "insertUnorderedList", icon: List, label: "Bullet list" },
        { cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered list" },
        { cmd: "hiliteColor", arg: "#E5A90033", icon: Highlighter, label: "Highlight" },
        { cmd: "justifyLeft", icon: AlignLeft, label: "Align left" },
        { cmd: "justifyCenter", icon: AlignCenter, label: "Align centre" },
    ];

    return (
        <div className="border border-white/12 bg-bg0" data-testid={testId}>
            <div className="flex flex-wrap items-center gap-1 border-b border-white/10 px-2 py-1.5">
                {TOOLS.map((t) => (
                    <button key={t.label} type="button" title={t.label} onClick={() => exec(t.cmd, t.arg)}
                        data-testid={`${testId}-${t.cmd}`}
                        className="p-1.5 text-zinc-400 hover:text-gold hover:bg-white/5 transition-colors">
                        <t.icon className="w-3.5 h-3.5" />
                    </button>
                ))}
                <button type="button" title="Insert link" onClick={openLink} data-testid={`${testId}-link`}
                    className="p-1.5 text-zinc-400 hover:text-gold hover:bg-white/5"><Link2 className="w-3.5 h-3.5" /></button>
                <button type="button" title="Clear formatting" onClick={() => exec("removeFormat")}
                    className="ml-auto mono text-[9px] tracking-[0.14em] text-zinc-500 hover:text-white px-2">CLEAR</button>
            </div>
            <div ref={ref} contentEditable suppressContentEditableWarning data-testid={`${testId}-area`}
                data-placeholder={placeholder}
                onInput={(e) => onChange(e.currentTarget.innerHTML)}
                onBlur={(e) => onChange(e.currentTarget.innerHTML)}
                className="blog-html rte-area px-3 py-3 text-sm text-zinc-200 outline-none"
                style={{ minHeight }} />
            {linkOpen && <LinkPicker onClose={() => setLinkOpen(false)} onPick={applyLink} />}
        </div>
    );
};

// ---------------------------------------------------------------- internal link picker
export const LinkPicker = ({ onPick, onClose }) => {
    const [q, setQ] = useState("");
    const [href, setHref] = useState("");
    const { data: posts = [] } = useQuery({ queryKey: ["blog-link-posts"], queryFn: async () => (await rhm.get("/blog/posts?status=all")).data });
    const { data: products = [] } = useQuery({ queryKey: ["link-products"], queryFn: async () => (await rhm.get("/products")).data });

    const options = [
        { group: "Pages", items: [
            { label: "Shop", href: "/shop" }, { label: "Consultation", href: "/consultation" },
            { label: "Science", href: "/science" }, { label: "Journal", href: "/blog" },
        ] },
        { group: "Products", items: products.map((p) => ({ label: p.name, href: `/product/${p.slug}` })) },
        { group: "Articles", items: posts.map((p) => ({ label: p.title, href: `/blog/${p.slug}` })) },
    ];

    return (
        <Modal open onClose={onClose} title="Insert link" testId="link-picker">
            <Field label="Search ReHealthMe">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Product, article or page"
                    data-testid="link-picker-search" />
            </Field>
            <div className="mt-4 max-h-64 overflow-y-auto space-y-4">
                {options.map((o) => {
                    const items = o.items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase()));
                    if (!items.length) return null;
                    return (
                        <div key={o.group}>
                            <div className="mono text-[9px] tracking-[0.2em] text-zinc-500 mb-2">{o.group.toUpperCase()}</div>
                            <div className="space-y-1">
                                {items.slice(0, 12).map((i) => (
                                    <button key={i.href} onClick={() => onPick(i.href, i.label)}
                                        data-testid={`link-option-${i.href}`}
                                        className="w-full text-left px-3 py-2 border border-white/10 hover:border-gold text-sm">
                                        {i.label} <span className="text-zinc-500 text-xs">{i.href}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="mt-5 pt-4 border-t border-white/10 flex items-end gap-2">
                <Field label="Or paste any address" className="flex-1">
                    <Input value={href} onChange={(e) => setHref(e.target.value)} placeholder="https://…"
                        data-testid="link-picker-url" />
                </Field>
                <Btn onClick={() => href && onPick(href, "")} data-testid="link-picker-apply">INSERT</Btn>
            </div>
        </Modal>
    );
};

// ---------------------------------------------------------------- one block
export const BlockCard = ({ block, index, total, products, areas, posts, onChange, onMove, onDuplicate, onDelete }) => {
    const [mediaFor, setMediaFor] = useState(null);
    const def = BLOCK_TYPES.find((b) => b.type === block.type) || { label: block.type, icon: Type };
    const set = (patch) => onChange({ ...block, ...patch });

    return (
        <div className="border border-white/10 bg-bg1" data-testid={`block-${block.id}`}>
            <header className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                <def.icon className="w-3.5 h-3.5 text-gold shrink-0" />
                <span className="mono text-[10px] tracking-[0.16em] text-zinc-400 flex-1 truncate">
                    {def.label.toUpperCase()}
                </span>
                <button onClick={() => onMove(index, -1)} disabled={index === 0} data-testid={`block-up-${block.id}`}
                    className="text-zinc-500 hover:text-white disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => onMove(index, 1)} disabled={index === total - 1} data-testid={`block-down-${block.id}`}
                    className="text-zinc-500 hover:text-white disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => onDuplicate(index)} data-testid={`block-duplicate-${block.id}`}
                    className="text-zinc-500 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
                <button onClick={() => onDelete(index)} data-testid={`block-delete-${block.id}`}
                    className="text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </header>

            <div className="p-3 space-y-3">
                {block.type === "heading" && (
                    <div className="flex gap-2">
                        <Select value={String(block.level || 2)} onChange={(e) => set({ level: Number(e.target.value) })}
                            className="w-24" data-testid={`block-level-${block.id}`}
                            options={[{ value: "2", label: "H2" }, { value: "3", label: "H3" }, { value: "4", label: "H4" }]} />
                        <Input value={block.text || ""} onChange={(e) => set({ text: e.target.value })}
                            placeholder="Heading text" data-testid={`block-heading-${block.id}`} />
                    </div>
                )}

                {["text", "quote"].includes(block.type) && (
                    <RichText value={block.html} onChange={(html) => set({ html })} testId={`block-rich-${block.id}`}
                        placeholder={block.type === "quote" ? "A quote worth highlighting…" : "Write your paragraph here…"} />
                )}
                {block.type === "quote" && (
                    <Input value={block.cite || ""} onChange={(e) => set({ cite: e.target.value })}
                        placeholder="Attribution (optional)" data-testid={`block-cite-${block.id}`} />
                )}

                {["tip", "info", "warning", "cta"].includes(block.type) && (
                    <>
                        <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })}
                            placeholder="Box title" data-testid={`block-title-${block.id}`} />
                        <RichText value={block.html} onChange={(html) => set({ html })} minHeight={80}
                            testId={`block-rich-${block.id}`}
                            placeholder={block.type === "cta" ? "A short line of encouragement…" : "What should the reader know?"} />
                        {block.type === "cta" && (
                            <div className="grid grid-cols-2 gap-2">
                                <Input value={block.label || ""} onChange={(e) => set({ label: e.target.value })}
                                    placeholder="Button label" data-testid={`block-cta-label-${block.id}`} />
                                <Input value={block.href || ""} onChange={(e) => set({ href: e.target.value })}
                                    placeholder="/shop" data-testid={`block-cta-href-${block.id}`} />
                            </div>
                        )}
                    </>
                )}

                {block.type === "image" && (
                    <>
                        {block.url ? (
                            <div className="relative">
                                <img src={block.url} alt={block.alt || ""} className="w-full h-40 object-cover border border-white/10" />
                                <button onClick={() => setMediaFor("image")} data-testid={`block-image-change-${block.id}`}
                                    className="absolute top-2 right-2 bg-black/70 px-2 py-1 mono text-[9px] tracking-[0.14em] text-gold">CHANGE</button>
                            </div>
                        ) : (
                            <button onClick={() => setMediaFor("image")} data-testid={`block-image-pick-${block.id}`}
                                className="w-full border border-dashed border-white/20 py-8 text-sm text-zinc-400 hover:border-gold hover:text-gold">
                                <ImageIcon className="w-4 h-4 mx-auto mb-1" /> Choose or upload an image
                            </button>
                        )}
                        <Input value={block.alt || ""} onChange={(e) => set({ alt: e.target.value })}
                            placeholder="ALT text (describes the image)" data-testid={`block-image-alt-${block.id}`} />
                        <Input value={block.caption || ""} onChange={(e) => set({ caption: e.target.value })}
                            placeholder="Caption (optional)" data-testid={`block-image-caption-${block.id}`} />
                        <div className="grid grid-cols-2 gap-2">
                            <Select value={block.width || "full"} onChange={(e) => set({ width: e.target.value })}
                                data-testid={`block-image-width-${block.id}`}
                                options={[{ value: "full", label: "Full width" }, { value: "wide", label: "Wide" },
                                    { value: "inline", label: "Small" }]} />
                            <Select value={block.align || "center"} onChange={(e) => set({ align: e.target.value })}
                                data-testid={`block-image-align-${block.id}`}
                                options={[{ value: "center", label: "Centre" }, { value: "left", label: "Left" },
                                    { value: "right", label: "Right" }]} />
                        </div>
                        <Input value={block.link || ""} onChange={(e) => set({ link: e.target.value })}
                            placeholder="Link when clicked (optional)" data-testid={`block-image-link-${block.id}`} />
                    </>
                )}

                {block.type === "gallery" && (
                    <>
                        <div className="grid grid-cols-3 gap-2">
                            {(block.images || []).map((im, i) => (
                                <div key={i} className="relative">
                                    <img src={im.url} alt={im.alt || ""} className="w-full h-20 object-cover border border-white/10" />
                                    <button onClick={() => set({ images: block.images.filter((_, j) => j !== i) })}
                                        data-testid={`block-gallery-remove-${block.id}-${i}`}
                                        className="absolute top-1 right-1 bg-black/70 p-1 text-red-400"><Trash2 className="w-3 h-3" /></button>
                                </div>
                            ))}
                        </div>
                        <Btn variant="ghost" onClick={() => setMediaFor("gallery")} data-testid={`block-gallery-add-${block.id}`}>
                            + ADD IMAGE
                        </Btn>
                    </>
                )}

                {block.type === "video" && (
                    <>
                        <Input value={block.url || ""} onChange={(e) => set({ url: e.target.value })}
                            placeholder="YouTube or Vimeo link" data-testid={`block-video-url-${block.id}`} />
                        <Input value={block.caption || ""} onChange={(e) => set({ caption: e.target.value })}
                            placeholder="Caption (optional)" data-testid={`block-video-caption-${block.id}`} />
                    </>
                )}

                {block.type === "table" && (
                    <TableEditor block={block} set={set} />
                )}

                {block.type === "faq" && (
                    <>
                        <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })}
                            placeholder="FAQ heading" data-testid={`block-faq-title-${block.id}`} />
                        {(block.items || []).map((item, i) => (
                            <div key={i} className="border border-white/10 p-3 space-y-2">
                                <Input value={item.question} placeholder="Question" data-testid={`block-faq-q-${block.id}-${i}`}
                                    onChange={(e) => set({ items: block.items.map((it, j) => j === i ? { ...it, question: e.target.value } : it) })} />
                                <Textarea rows={2} value={item.answer} placeholder="Answer"
                                    data-testid={`block-faq-a-${block.id}-${i}`}
                                    onChange={(e) => set({ items: block.items.map((it, j) => j === i ? { ...it, answer: e.target.value } : it) })} />
                                <button onClick={() => set({ items: block.items.filter((_, j) => j !== i) })}
                                    className="mono text-[9px] tracking-[0.14em] text-red-400">REMOVE</button>
                            </div>
                        ))}
                        <Btn variant="ghost" data-testid={`block-faq-add-${block.id}`}
                            onClick={() => set({ items: [...(block.items || []), { question: "", answer: "" }] })}>
                            + ADD QUESTION
                        </Btn>
                    </>
                )}

                {block.type === "product" && (
                    <Select value={block.slug || ""} onChange={(e) => set({ slug: e.target.value })}
                        data-testid={`block-product-${block.id}`}
                        options={(products || []).map((p) => ({ value: p.slug, label: p.name }))} />
                )}

                {block.type === "consultation" && (
                    <>
                        <Input value={block.title || ""} onChange={(e) => set({ title: e.target.value })}
                            placeholder="Heading" data-testid={`block-consult-title-${block.id}`} />
                        <Textarea rows={2} value={block.body || ""} onChange={(e) => set({ body: e.target.value })}
                            placeholder="Short line of copy" data-testid={`block-consult-body-${block.id}`} />
                        <div className="grid grid-cols-2 gap-2">
                            <Input value={block.label || ""} onChange={(e) => set({ label: e.target.value })}
                                placeholder="Button label" data-testid={`block-consult-label-${block.id}`} />
                            <Select value={block.area || ""} onChange={(e) => set({ area: e.target.value })}
                                data-testid={`block-consult-area-${block.id}`}>
                                <option value="">General consultation</option>
                                {(areas || []).map((a) => <option key={a.key || a.id} value={a.key || a.id}>{a.name}</option>)}
                            </Select>
                        </div>
                    </>
                )}

                {block.type === "divider" && <div className="text-xs text-zinc-500">A thin line between sections.</div>}
            </div>

            {mediaFor && (
                <MediaLibrary folder="Blog" onClose={() => setMediaFor(null)}
                    onPick={(picked) => {
                        if (mediaFor === "gallery") set({ images: [...(block.images || []), picked] });
                        else set({ url: picked.url, alt: block.alt || picked.alt, caption: block.caption || picked.caption });
                        setMediaFor(null);
                    }} />
            )}
        </div>
    );
};

const TableEditor = ({ block, set }) => {
    const cols = block.columns || [];
    const rows = block.rows || [];
    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                {cols.map((c, i) => (
                    <Input key={i} value={c} data-testid={`table-col-${block.id}-${i}`}
                        onChange={(e) => set({ columns: cols.map((cc, j) => (j === i ? e.target.value : cc)) })} />
                ))}
                <Btn variant="ghost" data-testid={`table-add-col-${block.id}`}
                    onClick={() => set({ columns: [...cols, `Column ${cols.length + 1}`],
                        rows: rows.map((r) => [...r, ""]) })}>+ COL</Btn>
            </div>
            {rows.map((r, ri) => (
                <div key={ri} className="flex gap-2">
                    {r.map((cell, ci) => (
                        <Input key={ci} value={cell} data-testid={`table-cell-${block.id}-${ri}-${ci}`}
                            onChange={(e) => set({ rows: rows.map((rr, j) => j === ri
                                ? rr.map((cc, k) => (k === ci ? e.target.value : cc)) : rr) })} />
                    ))}
                    <button onClick={() => set({ rows: rows.filter((_, j) => j !== ri) })}
                        className="text-zinc-500 hover:text-red-400 px-2"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
            ))}
            <Btn variant="ghost" data-testid={`table-add-row-${block.id}`}
                onClick={() => set({ rows: [...rows, cols.map(() => "")] })}>+ ROW</Btn>
        </div>
    );
};

export const AddBlockBar = ({ onAdd }) => {
    const [open, setOpen] = useState(false);
    const groups = [...new Set(BLOCK_TYPES.map((b) => b.group))];
    return (
        <div data-testid="add-block-bar">
            <button onClick={() => setOpen((v) => !v)} data-testid="add-block-toggle"
                className="w-full border border-dashed border-white/20 py-3 mono text-[10px] tracking-[0.18em]
                           text-zinc-400 hover:border-gold hover:text-gold transition-colors">
                + ADD BLOCK
            </button>
            {open && (
                <div className="mt-2 border border-white/10 bg-bg1 p-3 space-y-3">
                    {groups.map((g) => (
                        <div key={g}>
                            <div className="mono text-[9px] tracking-[0.2em] text-zinc-500 mb-2">{g.toUpperCase()}</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {BLOCK_TYPES.filter((b) => b.group === g).map((b) => (
                                    <button key={b.type} data-testid={`add-block-${b.type}`}
                                        onClick={() => { onAdd(b.type); setOpen(false); }}
                                        className="flex items-center gap-2 border border-white/10 px-3 py-2 text-sm
                                                   hover:border-gold hover:text-gold transition-colors">
                                        <b.icon className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{b.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
